import { useEffect, useState } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetApiCarrousel, useGetApiAccountKycStatus, useGetApiDashboardEvo } from '@/api';
import { getGetApiDashboardEvoQueryKey } from '@/api/dashboard/dashboard';
import ArbitrageSlider from '@/components/ArbitrageSlider/ArbitrageSlider';
import CardInProgress from '@/components/CardInProgress/CardInProgress';
import CardProjectInvestment from '@/components/CardProjectInvestment/CardProjectInvestment';
import CardResumeSubscription from '@/components/CardResumeSubscription/CardResumeSubscription';
import Carrousel from '@/components/Carrousel/Carrousel';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import ContractGridCard from '@/components/ContractGridCard/ContractGridCard';
import DemoTag from '@/components/DemoTag/DemoTag';
import FixedActionsBar from '@/components/FixedActionsBar/FixedActionsBar';
import LineChart from '@/components/LineChart/LineChart';
import { dataButtonFiltre } from '@/components/LineChart/lineChartButtons';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { NotificationCenter } from '@/components/NotificationCenter/NotificationCenter';
import { TrendingValue } from '@/components/TrendingValue/TrendingValue';
import WarningKyc from '@/components/WarningKyc/warningKyc';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { chartDataProspect } from '@/constants/chartDataProspect';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTE } from '@/constants/route';
import { CONTRACT_FAMILY } from '@/enums/contractFamily';
import { TIMESPAN } from '@/enums/timespan';
import { MAJOR_ADULT_LEGAL_CAPACITY } from '@/enums/userLegalCapacity';
import { USER_TYPE } from '@/enums/userType';
import { useDemoContext } from '@/hooks/DemoContext';
import { useAuth } from '@/hooks/useAuth';
import useCancellationHandler from '@/hooks/useCancellationHandler';
import { useDashboardContratsCount } from '@/hooks/useDashboardContratsCount';
import { useListingContratsCount } from '@/hooks/useListingContratsCount';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalKyc from '@/modals/ContentModalKyc/ContentModalKyc';
import ContentModalRenonciation from '@/modals/ContentModalRenonciation/ContentModalRenonciation';
import { ContentModalVersementLivret } from '@/modals/ContentModalVersementLivret/ContentModalVersementLivret';
import { useUserStore } from '@/store/useStore';
import { getContratStatut } from '@/utils/functions/getContratStatut/getContratStatut';
import { performanceFilter } from '@/utils/functions/performanceFilter/performanceFilter';

import styles from './styles.module.css';

export const Dashboard: React.FC = () => {
  const { windowWidth } = useScreenSize();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, updateUser, logout } = useAuth();
  const { data: kycStatus } = useGetApiAccountKycStatus({
    query: {
      enabled: user?.type === USER_TYPE.client,
    },
  });

  const clearUser = useUserStore((state) => state.clearUser);
  const { isDemo } = useDemoContext();
  const [prospectText, setProspectText] = useState('');
  const [lineChartTimespan, setLineChartTimespan] = useState<TIMESPAN>(TIMESPAN.allTime);
  const lineChartButtonFiltre = [...dataButtonFiltre];
  const { data, isLoading, isError, isSuccess } = useGetApiDashboardEvo();
  const { data: contratsCount } = useListingContratsCount();
  const contratsDashboardCount = useDashboardContratsCount();

  const { data: carrouselData, isLoading: isCarrouselLoading } = useGetApiCarrousel();

  const [performance, setPerformance] = useState<number | null>(0);
  const [plusMoinsValue, setPlusMoinsValue] = useState<number | null>(0);
  const [isModalKycOpen, setIsModalKycOpen] = useState(false);
  const [modaleKycVariant, setModaleKycVariant] = useState<'primary' | 'secondary'>('primary');
  const { showCancelModal, selectedContract, getCancellationHandler, handleCloseCancelModal } =
    useCancellationHandler();
  const queryClient = useQueryClient();
  const [showModalVersementLivret, setShowModalVersementLivret] = useState(false);
  const { filterPerformanceDashboard } = performanceFilter(data);
  const userConseiller = useUserStore((state) => state.user);
  if (sessionStorage.parcoursVersementData) {
    sessionStorage.removeItem('parcoursVersementData');
  }

  useEffect(() => {
    if (!data || !isSuccess) return;

    const { status, prospectKey } = getContratStatut(data);
    sessionStorage.setItem('user_statut_contrat', status);
    setProspectText(prospectKey ? t(prospectKey) : '');

    if (performance === 0 && plusMoinsValue === 0) {
      setPerformance(data?.evolution?.performance ?? 0);
      setPlusMoinsValue(data?.evolution?.plusMoinsValue ?? 0);
    }
  }, [data, isSuccess, performance, plusMoinsValue]);

  useEffect(() => {
    if (data) {
      filterPerformanceDashboard({ lineChartTimespan, setPerformance, setPlusMoinsValue });
    }
  }, [data, lineChartTimespan, filterPerformanceDashboard]);

  useEffect(() => {
    globalThis.scrollTo({ top: 0, behavior: 'smooth' });

    if (isSuccess && user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'mon portefeuille',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'mon portefeuille',
        event_page: 'mon portefeuille',
      });
    }
  }, [isSuccess, user?.id, user?.type, user?.success]);

  useEffect(() => {
    if (kycStatus?.statutKyc === 'KO' && user?.justLoggedIn) {
      setIsModalKycOpen(true);
      updateUser({ justLoggedIn: false });

      void TRACKING_INSTANCE.triggerEvent('action', undefined, {
        event_id: 'action',
        event_categorie:
          user.capaciteJuridique && user.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY
            ? 'affichage popin mettre a jour informations'
            : 'affichage popin données personnelles mineur',
        event_nom:
          user.capaciteJuridique && user.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY
            ? 'affichage popin mettre a jour informations'
            : 'affichage popin données personnelles mineur',
        event_metier: 'espace client placement',
        event_detail_1: 'cta',
      });
    }
  }, [kycStatus?.statutKyc, user, updateUser]);

  useEffect(() => {
    if (!isError) return;
    clearUser();
    logout();
    navigate(ROUTE.ERREUR);
  }, [isError, clearUser, logout, navigate]);

  if (isError) return null;
  if (!data || !user || isLoading) return <Loader />;

  const fallbackTotalContrats =
    (data?.listContrat?.enCours?.length ?? 0) + (data?.listContrat?.enTraitement?.length ?? 0);
  // Total affiché : tous les contrats en cours, SCPI et défiscalisation comprises.
  const totalContrats = contratsDashboardCount?.total ?? fallbackTotalContrats;
  // Versement direct : uniquement les contrats arbitrables de /listingcontrats.
  const totalContratsVersables = contratsCount
    ? Object.values(contratsCount).reduce((sum, n) => sum + n, 0)
    : fallbackTotalContrats;
  const enCoursDisplay = (data?.listContrat?.enCours ?? []).slice(0, 3);
  const enTraitementDisplay = (data?.listContrat?.enTraitement ?? []).slice(0, 3 - enCoursDisplay.length);

  const date = new Date();
  const dateOuverture = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}/${date.getFullYear()}`;

  const displayCardVersment = () => {
    if (user?.type !== USER_TYPE.client) {
      return (
        <MtxGrid row>
          <MtxGrid col colXs={12} colSm={12} colMd={12} colLg={10} colXl={10}>
            <h2>{t('dashboard.label')}</h2>
            <CardProjectInvestment />
          </MtxGrid>
        </MtxGrid>
      );
    }
  };

  const displayPerformance = () => {
    if (
      data?.listContrat?.enCours?.some((contrat) =>
        [
          CONTRACT_FAMILY.ASSURANCEVIE,
          CONTRACT_FAMILY.RETRAITE,
          CONTRACT_FAMILY.LIVRET,
          CONTRACT_FAMILY.DEFISCALISATION,
        ].includes(contrat.famille as CONTRACT_FAMILY)
      )
    ) {
      return (
        <TrendingValue
          value={plusMoinsValue}
          variant="dashboard"
          secondaryValue={performance}
          icon={plusMoinsValue !== null}
        />
      );
    }
  };
  const getButtonText = () => {
    if (totalContrats < 4) {
      return '';
    }
    if (windowWidth < 600) {
      return 'Voir tout';
    }
    return 'Voir tous mes contrats';
  };

  const shouldDisplayPerformance =
    contratsDashboardCount !== undefined &&
    !contratsDashboardCount.byFamille.IMMOBILIER &&
    !contratsDashboardCount.byFamille.BOURSE;

  const depositEligibleFamilies = new Set([
    CONTRACT_FAMILY.ASSURANCEVIE,
    CONTRACT_FAMILY.RETRAITE,
    CONTRACT_FAMILY.LIVRET,
  ]);
  const hasEligibleContract =
    data?.listContrat?.enCours?.some((c) => depositEligibleFamilies.has(c.famille as CONTRACT_FAMILY)) ?? false;
  const contratLivret = data?.listContrat?.enCours?.filter((c) => c.famille === CONTRACT_FAMILY.LIVRET).length ?? 0;
  const firstContract = data?.listContrat?.enCours?.[0] ?? null;

  const handleDepositClick = () => {
    if (totalContratsVersables === 1 && contratLivret === 1) {
      setShowModalVersementLivret(true);
    } else if (totalContratsVersables === 1 && depositEligibleFamilies.has(firstContract?.famille as CONTRACT_FAMILY)) {
      navigate(ROUTE.SELECTOPERATION, {
        state: { key: 'versement', infoContrat: firstContract },
      });
    } else {
      navigate(ROUTE.SELECTCONTRACT, { state: { key: 'versement' } });
    }
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'faire un versement',
      event_detail_1: 'cta bandeau',
    });
  };

  return (
    <>
      {showCancelModal && selectedContract && (
        <ContentModalRenonciation
          nomProduit={selectedContract.nomProduit}
          placementId={selectedContract.placementId}
          delayDays={selectedContract.cancelablePeriod ?? 30}
          onClose={handleCloseCancelModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: getGetApiDashboardEvoQueryKey() });
            handleCloseCancelModal();
          }}
        />
      )}

      {isModalKycOpen && (
        <ContentModalKyc
          onClickClose={() => setIsModalKycOpen(false)}
          WarningIcon={true}
          legalCapacity={user?.capaciteJuridique}
          variant={modaleKycVariant}
          location="dashboard"
        />
      )}
      {showModalVersementLivret && (
        <ModaleDefaut
          title={t('depositCard.versementLivretModalTitle')}
          content={ContentModalVersementLivret}
          btnLabel={t('depositCard.versementLivretModalBtnLabel')}
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => setShowModalVersementLivret(false)}
          btnFullwidth={false}
          onClickClose={() => setShowModalVersementLivret(false)}
          mobileStyle="stick-to-bottom"
          size="large"
          infoCircle={true}
        />
      )}
      <div className={styles.dashboard}>
        <div className={styles.dashboardHead}>
          {windowWidth > BREAKPOINTS.tablet && <NotificationCenter />}
          {FEATURE_FLAGS.ARBITRAGE && windowWidth > BREAKPOINTS.tablet && <ArbitrageSlider />}
          {kycStatus?.statutKyc === 'KO' && (
            <WarningKyc
              onClickShowModal={() => {
                setModaleKycVariant('secondary');
                setIsModalKycOpen(true);
              }}
              legalCapacity={user?.capaciteJuridique}
            />
          )}

          {user?.type !== USER_TYPE.prospect && (
            <div className={styles.dashboardHeader}>
              <h2 className={styles.dashboardGreeting}>
                {t('common.welcome')} <span className="prenom-dashboard">{user?.prenom}</span>,
              </h2>
              {user?.type === USER_TYPE.client && hasEligibleContract && BREAKPOINTS.mobile <= windowWidth && (
                <MtxButton
                  size="medium"
                  variant="primary"
                  disabled={!!userConseiller?.advisorView}
                  onClick={handleDepositClick}
                >
                  {t('depositCard.buttonText')}
                </MtxButton>
              )}
            </div>
          )}

          <div
            className={`${styles.dashboardHeadContent} ${
              user.type &&
              [USER_TYPE.prospect, USER_TYPE.formerClient, USER_TYPE.subscriber].includes(user.type as USER_TYPE)
                ? styles.prospect
                : ''
            }`}
          >
            {user.type &&
              [USER_TYPE.prospect, USER_TYPE.formerClient, USER_TYPE.subscriber].includes(user.type as USER_TYPE) && (
                <span className={styles.prospectHeadText}>{prospectText}</span>
              )}

            <div className={styles.portfolio}>
              <h1>{t('common.myWallet')}</h1>
            </div>

            <div className={styles.valuationInfo}>
              <span className={styles.valuation}>
                {t('format.currencyFlexible', { value: data?.valorisation?.encours ?? 0 })}
                {isDemo && <DemoTag />}
              </span>

              <p className={styles.valuationDate}>Au {dateOuverture}</p>
            </div>
            {shouldDisplayPerformance && displayPerformance()}
            <div className={`chart-content ${styles.chartContent}`}>
              <LineChart
                timespan={lineChartTimespan}
                donnees={user?.type === USER_TYPE.client ? (data?.chartData ?? []) : chartDataProspect}
              />

              <div className={`${styles.contentButtonFiltre} ${styles.filterButtonContent}`}>
                {lineChartButtonFiltre.map((value, index, buttonsFiltre) => (
                  <ChipsRadioSmall
                    key={value.value}
                    label={windowWidth < 600 ? value.textMobile : value.text}
                    name="lineChartButtons"
                    value={value?.value}
                    defaultCheck={index + 1 === buttonsFiltre.length}
                    onClick={() => {
                      setLineChartTimespan(value?.value);
                      filterPerformanceDashboard({
                        lineChartTimespan: value?.value,
                        setPerformance,
                        setPlusMoinsValue,
                      });
                      void TRACKING_INSTANCE.triggerEvent('clic', undefined, value.tracking);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        {(data?.listContrat?.enTraitement?.length ?? 0) > 0 && (data?.listContrat?.enCours?.length ?? 0) === 0 && (
          <div className={styles.dashboardSection}>
            <h2>
              {t('dashboard.contractsInProgress', {
                count: data?.listContrat?.enTraitement?.length,
              })}
            </h2>

            <MtxGrid className={styles.contractsCards} row>
              {data?.listContrat?.enTraitement?.map((contrat) => {
                return (
                  <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4} key={contrat.placementId}>
                    <CardInProgress
                      famille={contrat.famille}
                      titreProduit={contrat.nomProduit ?? ''}
                      iconAssurance={contrat.gestionnaireLogo ?? ''}
                      cancelablePeriod={contrat.cancelablePeriod ?? 30}
                      onRequestCancellation={getCancellationHandler({
                        ...contrat,
                        isCancelable: true,
                      })}
                    />
                  </MtxGrid>
                );
              })}
            </MtxGrid>
          </div>
        )}

        {displayCardVersment()}

        {!!carrouselData?.carrousel?.length && !isCarrouselLoading && (
          <div className={styles.dashboardSection}>
            <h2>{t('dashboard.exploreProductsLabel')}</h2>

            <MtxGrid row>
              <MtxGrid col={8} colXs={12} colSm={12} colMd={12} colLg={10}>
                <Carrousel data={carrouselData.carrousel} isLoading={isCarrouselLoading} />
              </MtxGrid>
            </MtxGrid>
          </div>
        )}

        {(data?.listContrat?.enCours?.length ?? 0) > 0 && (
          <div className={styles.dashboardSection}>
            <div className={styles.contractsHead}>
              <h2>
                {t('dashboard.contracts', {
                  count: totalContrats,
                })}
                {isDemo && <DemoTag />}
              </h2>

              {user?.type === USER_TYPE.client && (
                <button
                  className={styles.viewAllContracts}
                  onClick={() => {
                    navigate('/contrats');
                    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                      event_id: 'clic',
                      event_categorie: 'clic',
                      event_metier: 'espace client placement',
                      event_nom: 'voir tous mes contrats',
                      event_detail_1: 'lien textuel',
                    });
                  }}
                >
                  {getButtonText()}
                </button>
              )}
            </div>
            {FEATURE_FLAGS.ARBITRAGE && windowWidth <= BREAKPOINTS.tablet && <ArbitrageSlider />}
            <MtxGrid className={styles.contractsCards} row>
              {enTraitementDisplay.map((contrat, index) => {
                return (
                  <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4} key={contrat.placementId || index}>
                    <CardInProgress
                      famille={contrat.famille}
                      titreProduit={contrat.nomProduit ?? ''}
                      iconAssurance={contrat.gestionnaireLogo ?? ''}
                      cancelablePeriod={contrat.cancelablePeriod ?? 30}
                      onRequestCancellation={getCancellationHandler({
                        ...contrat,
                        isCancelable: true,
                      })}
                    />
                  </MtxGrid>
                );
              })}

              {enCoursDisplay.map((contract) => (
                <ContractGridCard
                  key={contract.placementId}
                  contract={contract}
                  onRequestCancellation={getCancellationHandler(contract)}
                />
              ))}
            </MtxGrid>
          </div>
        )}
        {(data?.aFinaliser?.length ?? 0) > 0 && data.aFinaliser !== null ? (
          <div className={styles.dashboardSection}>
            <h2>{t('dashboard.subtitle')}</h2>

            <MtxGrid className={styles.subscriptionsCards} row>
              {data?.aFinaliser?.map((souscription) => {
                return (
                  <MtxGrid
                    col={4}
                    colXs={12}
                    colSm={6}
                    colMd={6}
                    colLg={4}
                    key={`  ${souscription.famille}-
                              ${souscription.dateDebutSouscription}-
                              ${souscription.nomProduit}-
                              ${souscription.token}`}
                  >
                    <CardResumeSubscription
                      completed={souscription.pourcentageAvancement ?? 0}
                      famille={souscription.famille}
                      NomContratCard={souscription.nomProduit}
                      lienDeReprise={souscription.link ?? undefined}
                      dateDebutSouscription={souscription.dateDebutSouscription ?? ''}
                      token={souscription.token ?? undefined}
                      headerName={souscription.headerName ?? undefined}
                    />
                  </MtxGrid>
                );
              })}
            </MtxGrid>
          </div>
        ) : null}
        {user?.type === USER_TYPE.client && hasEligibleContract && BREAKPOINTS.mobile > windowWidth && (
          <FixedActionsBar>
            <MtxButton
              size="large"
              variant="primary"
              disabled={!!userConseiller?.advisorView}
              fullWidth
              onClick={handleDepositClick}
            >
              {t('depositCard.buttonText')}
            </MtxButton>
          </FixedActionsBar>
        )}
      </div>
    </>
  );
};
